import { getLocale } from 'common/functions.js'
import { useSiteInfoStore } from 'stores/site-info/index.js'
import { useSystemStore } from 'stores/system'
import { AUTOLOGIN_TOKEN_PARAM } from 'src/common/autologin/constants'
import { localeForExternalService } from 'src/common/utils/locale'
import { getConfig } from 'src/configs'

const config = getConfig()

const AFFILKA = [
  {
    origin: 'stag',
    replaceable: 'stag',
  },
]

const UTM_MARKERS = [
  {
    origin: 'utm_source',
    replaceable: 'pid',
  },
  {
    origin: 'utm_medium',
    replaceable: 'af_channel',
  },
  {
    origin: 'utm_campaign',
    replaceable: 'c',
  },
  {
    origin: 'utm_content',
    replaceable: 'af_adset',
  },
  {
    origin: 'utm_term',
    replaceable: 'af_ad',
  },
]
const CLICK_ID = [
  {
    origin: 'pid',
    replaceable: 'pid',
  },
  {
    origin: 'source',
    replaceable: 'af_channel',
  },
  {
    origin: 'qtag',
    replaceable: 'af_adset',
  },
  {
    origin: 'partner',
    replaceable: 'af_adset',
  },
  {
    origin: 'click_id',
    replaceable: 'af_ad',
  },
]
const DWH_MARKERS = [
  {
    origin: 'click_id',
    replaceable: 'clickId',
  },
  {
    origin: 'pid',
    replaceable: 'partnerId',
  },
  {
    origin: 'partner',
    replaceable: 'partnerName',
  },
  {
    origin: 'sub_id',
    replaceable: 'subId',
  },
  {
    origin: 'source',
    replaceable: 'partnerSource',
  },
  {
    origin: 'modal',
    replaceable: 'modal',
  },
  {
    origin: 'utm_source',
    replaceable: 'utmSource',
  },
  {
    origin: 'utm_medium',
    replaceable: 'utmMedium',
  },
  {
    origin: 'utm_campaign',
    replaceable: 'utmCampaign',
  },
  {
    origin: 'utm_content',
    replaceable: 'utmContent',
  },
  {
    origin: 'utm_term',
    replaceable: 'utmTerm',
  },
]
export const LINKS = () => {
  const siteInfoStore = useSiteInfoStore()

  return {
    ios: siteInfoStore.getMobileAppUrlIos,
    android: siteInfoStore.getMobileAppUrlAndroid,
    default: siteInfoStore.getMobileAppUrl,
  }
}

const LINKS_DEFAULT_FIELD = config.app.deepLinks.default
const DEEP_LINK_PLATFORMS = config.app.deepLinks.platforms
const DEFAULT_DEEP_LINK = 'popmma://news'
const AUTOLOGIN_DEEP_LINK = 'olymp://login'

class AppsFlyerParseLink {
  constructor(os = LINKS_DEFAULT_FIELD, queries = window.location.search, deepLink = DEFAULT_DEEP_LINK) {
    this._os = os
    this._deepLink = deepLink
    this._queries = new URLSearchParams(queries)
    this._platform = process.env.PLATFORM
  }

  set os(value) {
    this._os = value
  }

  set queries(value) {
    this._queries = new URLSearchParams(value)
  }

  set deepLink(value) {
    this._deepLink = value
  }

  #deepLinkBuilder(autologinToken) {
    const deep_link = autologinToken ? AUTOLOGIN_DEEP_LINK : this._deepLink
    const isParams = this._queries.toString() !== ''
    const params = new URLSearchParams(isParams ? this._queries : { pid: window.location.hostname })

    if (autologinToken) {
      params.set(AUTOLOGIN_TOKEN_PARAM, autologinToken)
    }

    const deep_link_value = `${deep_link}/?${params}`

    return { deep_link_value }
  }

  // Убирает дубли из массива
  #uniq(arr) {
    return [...new Set(arr)]
  }

  /**
   * Создает колекцию из query
   * Тоже самое, что и ref_data из кук, но тут точнее и быстрее приходят данные на разных вкладках
   */
  #queryCollector() {
    const urlParams = {}

    for (const [key, value] of this._queries) {
      if (urlParams[key] === null) {
        return
      }

      // Проверяем есть ли дубли в ключах, если есть меняем тип значения ключа на массив
      if (typeof urlParams[key] !== 'undefined') {
        const uniqQueryUrlParams = this.#uniq([urlParams[key], value].flat(Infinity))
        // Если uniqQueryUrlParams больше 1, оставлем массив, если он меньше или равен 1, то превращаем в строку
        urlParams[key] = uniqQueryUrlParams.length > 1 ? uniqQueryUrlParams : uniqQueryUrlParams.join()
      } else {
        urlParams[key] = value
      }
    }

    return urlParams
  }

  // Определяем тип меток UTM и CLICK_ID
  #nameUtmOrClickId() {
    const urlParams = []

    for (const query of this._queries) {
      urlParams.push(query[0])
    }

    const isUtm = UTM_MARKERS.find((utm) => urlParams.includes(utm.origin))
    const isClick_id = CLICK_ID.find((click_id) => urlParams.includes(click_id.origin))
    const isAffilka = AFFILKA.find((affilka) => urlParams.includes(affilka.origin))

    if (isUtm) {
      return 'UTM_MARKERS'
    }
    if (isAffilka) {
      return 'AFFILKA'
    }
    if (isClick_id) {
      return 'CLICK_ID'
    }
  }

  // Создает объект объектов из массива, где index объекта является origin
  #getReplaceableMarker(markers) {
    return markers.reduce((acc, marker) => {
      acc[marker.origin] = marker.replaceable
      return acc
    }, {})
  }

  changeKeysOfQueries(markers) {
    // Меняем Markers origin на Markers replaceable
    return Object.entries(this.#queryCollector()).reduce((acc, query) => {
      // Находим нужный replaceable по origin ключу в объекте UTM_MARKERS
      const marker = this.#getReplaceableMarker(markers)

      // Проверяем на существующие значения
      if (typeof marker[query[0]] === 'undefined') {
        return acc
      }

      acc[marker[query[0]]] = query[1]
      return acc
    }, {})
  }

  queriesForAffilka() {
    const query = this.#queryCollector()
    const stagParts = query.stag?.split('_') || []

    return {
      af_adset: query.stag,
      af_channel: query.source,
      pid: stagParts[0],
      af_ad: stagParts[1],
      c: this._queries.toString(),
    }
  }

  #link() {
    return LINKS()[this._os] || LINKS().default
  }

  #parseLink() {
    const url = new URL(this.#link(), window.location.origin)

    if (url.origin === window.location.origin && !url.pathname.endsWith('/')) {
      url.pathname = `${url.pathname}/`
    }

    return url
  }

  #appendLanguage(url, lang) {
    if (!lang) {
      return url
    }

    const parsedUrl = new URL(url)
    parsedUrl.searchParams.set('language', lang)

    return parsedUrl.toString()
  }

  // Создаем новый URL со своими Queries
  queryBuilder() {
    let params = null

    if (this.#nameUtmOrClickId() === 'CLICK_ID') {
      const result = this.changeKeysOfQueries(CLICK_ID)
      const click_id = new URLSearchParams(this.#queryCollector())
      params = new URLSearchParams({ ...result, c: click_id })
    }

    if (this.#nameUtmOrClickId() === 'UTM_MARKERS') {
      const result = this.changeKeysOfQueries(UTM_MARKERS)
      params = new URLSearchParams(result)
    }

    if (this.#nameUtmOrClickId() === 'AFFILKA') {
      const result = this.queriesForAffilka()
      params = new URLSearchParams(result)
    }

    const url = this.#parseLink()
    params?.forEach((value, key) => {
      url.searchParams.set(key, value)
    })

    return url.toString()
  }

  getParseLink({ autologinToken = null } = {}) {
    const isQueriesNull = this._queries.toString() !== ''
    const isDeepLinkPlatform = DEEP_LINK_PLATFORMS.includes(this._os)
    const unknownMarkers = this.#nameUtmOrClickId() === undefined

    let resultUrl = this.#parseLink()

    if (isQueriesNull && !unknownMarkers) {
      const builtUrl = new URL(this.queryBuilder())
      builtUrl.searchParams.forEach((value, key) => {
        resultUrl.searchParams.set(key, value)
      })
    }

    if (isDeepLinkPlatform) {
      const deepLinkParams = this.#deepLinkBuilder(autologinToken)
      Object.entries(deepLinkParams).forEach(([key, value]) => {
        resultUrl.searchParams.set(key, value)
      })
    }

    const lang = localeForExternalService(getLocale(), {
      platform: useSystemStore().getPlatformName,
    })
    resultUrl = new URL(this.#appendLanguage(resultUrl.toString(), lang))

    return resultUrl.toString()
  }

  getDataDWH() {
    return this.changeKeysOfQueries(DWH_MARKERS)
  }

  updateAppLink(platform, os, link) {
    if (platform === this._platform) {
      useSiteInfoStore().updateMobileAppUrlIos(link)
    }
  }
}

const afpl = new AppsFlyerParseLink()

export default afpl
